import dayjs from 'dayjs';

export function toNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
}

export function formatCurrency(value) {
  const n = toNumber(value);
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 2,
  }).format(n);
}

export function formatAmount(value) {
  return new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(toNumber(value));
}

export function formatDate(value) {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY');
}

export function formatDateTime(value) {
  if (!value) return '—';
  return dayjs(value).format('DD MMM YYYY HH:mm');
}

export function apiErrMsg(err) {
  return err?.response?.data?.message || err?.message || 'Something went wrong';
}

export function columnSortOrder(sortState, field) {
  if (sortState.sort_by !== field) return null;
  return sortState.sort_order === 'asc' ? 'ascend' : 'descend';
}

export function canDeleteDealSheet(row) {
  return row?.payment_status === 'UNPAID' && toNumber(row?.payments_count) === 0;
}

export function isFinancialLocked(dealSheet) {
  return toNumber(dealSheet?.total_paid_amount) > 0;
}

export function isFullyPaid(dealSheet) {
  return dealSheet?.payment_status === 'PAID';
}

export function canEditDealSheet(dealSheet) {
  return !isFullyPaid(dealSheet);
}

export function getRemainingPayable(dealSheet) {
  const total = toNumber(dealSheet?.total_payable);
  const paid = toNumber(dealSheet?.total_paid_amount);
  return Math.max(0, total - paid);
}

export function validatePartialPaidAmount(paidAmount, totalPayable) {
  const paid = toNumber(paidAmount);
  const total = toNumber(totalPayable);
  if (paid <= 0) return 'Paid amount must be greater than zero';
  if (paid >= total) return 'Paid amount must be less than total payable for partial payment';
  return null;
}

export function validatePaymentAmount(amount, dealSheet, editingPayment = null) {
  const paymentAmount = toNumber(amount);
  const totalPayable = toNumber(dealSheet?.total_payable);
  const existingPaid = toNumber(dealSheet?.total_paid_amount);
  const editingAmount = editingPayment ? toNumber(editingPayment.amount) : 0;
  const projectedTotal = existingPaid - editingAmount + paymentAmount;

  if (paymentAmount <= 0) return 'Amount must be greater than zero';
  if (projectedTotal > totalPayable) {
    return `Total payments cannot exceed ${formatCurrency(totalPayable)}`;
  }
  return null;
}

export function buildCreatePayload(values, { supplierId, productId, isin, tradeCommitmentId }) {
  const payload = {
    supplier_id: supplierId,
    product_id: productId,
    transaction_type: values.transaction_type,
    transaction_date: values.transaction_date?.format?.('YYYY-MM-DD') ?? values.transaction_date,
    mode_of_delivery: values.mode_of_delivery,
    quantity: toNumber(values.quantity),
    price_per_share: toNumber(values.price_per_share),
    stamp_duty_percent: toNumber(values.stamp_duty_percent ?? 0),
    tds_percent: toNumber(values.tds_percent ?? 0),
    payment_status: values.payment_status || 'UNPAID',
  };

  if (isin) payload.isin = isin;
  if (tradeCommitmentId) payload.trade_commitment_id = tradeCommitmentId;

  const paymentDate = values.payment_date?.format?.('YYYY-MM-DD') ?? values.payment_date;
  if (paymentDate) payload.payment_date = paymentDate;

  if (values.utr_number) payload.utr_number = values.utr_number;
  if (values.remarks) payload.remarks = values.remarks;

  if (values.payment_status === 'PARTIAL') {
    payload.paid_amount = toNumber(values.paid_amount);
  }

  return payload;
}

export function resolveDealSheetId(dealSheet) {
  return dealSheet?.uuid ?? dealSheet?.id;
}

export const SUPPLIER_DEAL_SHEET_MAX_BYTES = 2 * 1024 * 1024;

export function filenameFromUrl(url) {
  if (!url) return '';
  try {
    return decodeURIComponent(url.split('/').pop() || '');
  } catch {
    return url.split('/').pop() || '';
  }
}
